import { selectCurrentHotelId } from 'accessors'
import { PMSs } from 'main/hotelBrandsAndChains'
import store from 'main/store'
import { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import dashboardApi from 'services/api/dashboard'
import LocalUserService from 'services/localUserService'
import { getSavedSubsidiary } from 'utils/helpers'

const REQUEST_AUTH_SNAPSHOT = 'request-auth-snapshot'

function pickDashboardUserId(raw) {
  if (raw == null) {
    return null
  }
  const n = typeof raw === 'number' ? raw : Number.parseInt(String(raw), 10)
  if (!Number.isFinite(n) || n <= 0) {
    return null
  }
  return n
}

/**
 * Re-pushes dashboard session from web localStorage into Electron main via IPC (no password).
 * Used when Electron store lost userToken/subsidiary but embedded app session is still valid.
 */
function pushAuthSnapshotToElectron() {
  if (!window?.electron?.postMessage) {
    return
  }
  const userService = new LocalUserService()
  const info = userService.getCurrentUserInfo()
  if (!info?.sessionId) {
    return
  }

  const loginState = store.getState().login
  const saved = getSavedSubsidiary()
  const subsidiaryId = loginState.currentHotelId || saved?.subsidiaryId || ''
  const hotelName = loginState.currentHotel || saved?.subsidiaryName || ''
  const dashId = pickDashboardUserId(info.dashboardUserId)

  if (subsidiaryId && info.user && info.userToken) {
    window.electron.postMessage({
      type: 'logged-in',
      data: {
        subsidiaryId: String(subsidiaryId).trim(),
        fullName: info.user.fullName || info.user.name || '',
        spAuth: info.sessionId,
        userId: String(info.userId != null ? info.userId : ''),
        ...(dashId != null ? { dashboardUserId: dashId } : {}),
        hotelName,
        userToken: info.userToken
      }
    })
    return
  }

  if (info.userToken) {
    window.electron.postMessage({
      type: 'token-updated',
      data: {
        spAuth: info.sessionId,
        userToken: info.userToken,
        ...(dashId != null ? { dashboardUserId: dashId } : {})
      }
    })
    return
  }

  window.electron.postMessage({
    type: 'token-updated',
    data: {
      spAuth: info.sessionId,
      ...(dashId != null ? { dashboardUserId: dashId } : {})
    }
  })
}

function useElectronMessageHandler() {
  const subsidiaryId = useSelector(selectCurrentHotelId)
  const pms = useSelector(s => s.hotelDetails.pms)
  const isAppReloading = useSelector(state => state.login.isAppReloading)
  const history = useHistory()

  const hasPepPMS = pms === PMSs.pep

  const handleLoginStatusPing = useCallback(async () => {
    try {
      const res = await dashboardApi.checkLogin()
      window.electron.postMessage({
        type: 'login-status-response',
        data: {
          loginStatus: res === 'alive' ? res : 'dead',
          subsidiaryId
        }
      })
    } catch (error) {
      window.electron.postMessage({
        type: 'login-status-response',
        data: {
          loginStatus: 'dead',
          error: error.message,
          subsidiaryId
        }
      })
    }
  }, [subsidiaryId])

  const handleSubsidiaryIdRequest = useCallback(async () => {
    try {
      const res = await dashboardApi.checkLogin()
      window.electron.postMessage({
        type: 'subsidiary-id',
        data: {
          subsidiaryId: res === 'alive' ? subsidiaryId : null
        }
      })
    } catch (error) {
      window.electron.postMessage({
        type: 'subsidiary-id',
        data: {
          subsidiaryId: null
        }
      })
    }
  }, [subsidiaryId])

  const handleNavigation = useCallback(
    async target => {
      if (!hasPepPMS) return

      try {
        history.push(target)

        // Send tracking message to Electron after navigation
        if (window?.electron?.postMessage) {
          window.electron.postMessage({
            type: 'navigation-completed',
            data: {
              destination: target,
              timestamp: new Date().toISOString(),
              currentUrl: window.location.href,
              isSuccess: true
            }
          })
        }

        if (target === '/pms-reports') {
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('triggerPepLogin'))
          }, 100)
        }
      } catch (error) {
        // Navigation failed, send error message
        if (window?.electron?.postMessage) {
          window.electron.postMessage({
            type: 'navigation-completed',
            data: {
              destination: target,
              timestamp: new Date().toISOString(),
              currentUrl: window.location.href,
              isSuccess: false
            }
          })
        }
      }
    },
    [hasPepPMS, history]
  )

  const handler = useCallback(
    async (_, event) => {
      switch (event.type) {
        case 'PING_LOGIN_STATUS':
          await handleLoginStatusPing()
          break

        case 'GET_SUBSIDIARY_ID':
          await handleSubsidiaryIdRequest()
          break

        case 'NAVIGATE_TO_PAGE':
          await handleNavigation(event.target)
          break

        case REQUEST_AUTH_SNAPSHOT:
          pushAuthSnapshotToElectron()
          break

        default:
          break
      }
    },
    [handleLoginStatusPing, handleSubsidiaryIdRequest, handleNavigation]
  )

  // Setup and cleanup electron message listener (runs without subsidiaryId so auth snapshot works).
  useEffect(() => {
    if (isAppReloading) return

    if (window.electron && typeof window.electron.onMessage === 'function') {
      window.electron.onMessage(handler)
      return () => {
        if (typeof window.electron.offMessage === 'function') {
          window.electron.offMessage(handler)
        }
      }
    }
  }, [handler, isAppReloading])
}

export default useElectronMessageHandler
